import React, { useMemo, useContext, useEffect, useState } from 'react';
import ActivationContext from './activation';

const BranchIOContext = React.createContext();

const parseBranchData = (branchData) => {
  if (!branchData) return null;
  if (branchData.data_parsed && typeof branchData.data_parsed === 'object') return branchData.data_parsed;
  if (branchData.data && typeof branchData.data === 'object') return branchData.data;
  if (typeof branchData.data === 'string') {
    try {
      return JSON.parse(branchData.data);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const extractActivationCode = (hostname, pathname) => {
  if (!pathname) return null;

  if (hostname === process.env.REACT_APP_DEFAULT_BRANCHIO_LINK_DOMAIN ||
    hostname === process.env.REACT_APP_DEFAULT_BRANCHIO_ALT_LINK_DOMAIN) {
    const code = pathname.startsWith('/') ? pathname.substring(10) : pathname;
    return code || null;
  }

  return null;
};

// webview mode
const minimal = new URLSearchParams(window.location.search).get('minimal');

export const BranchIOProvider = ({ children }) => {
  const [branchData, setBranchData] = useState(null);
  const activationContainer = useContext(ActivationContext);

  useEffect(() => {
    if (minimal === 'true') return;

    const key =
      process.env.REACT_APP_BRANCHIO_TEST_MODE === 'true'
        ? process.env.REACT_APP_BRANCH_IO_KEY_TEST
        : process.env.REACT_APP_BRANCH_IO_KEY_LIVE;

    window.branch.init(key, (error, data) => {
      if (error) console.log(error);

      console.log('branch data has been set');
      console.log('branch data:', data);

      setBranchData(data);
    });
  }, []);

  // ---- Extract activation code
  const activationCode = useMemo(() => {
    const parsed = parseBranchData(branchData);
    if (parsed) {
      const referring = parsed['~referring_link'];

      if (referring) {
        const url = new URL(referring);
        const code = extractActivationCode(url.hostname, url.pathname);
        if (code) return code;
      }
    }
    return null;
  }, [branchData]);

  useEffect(() => {
    if (!activationCode) return;
    activationContainer.set(activationCode);
  }, [activationCode, activationContainer]);

  // ---- Extract canonical URL
  const canonicalUrl = useMemo(() => {
    const parsed = parseBranchData(branchData);
    if (!parsed) return null;

    return parsed.$canonical_url || parsed.canonical_url || null;
  }, [branchData]);

  // ---- Redirect to canonical
  useEffect(() => {
    if (!canonicalUrl) return;

    if (window.location.href !== canonicalUrl) {
      window.location.replace(canonicalUrl);
    }
  }, [canonicalUrl]);

  const value = useMemo(() => ({ data: branchData }), [branchData]);

  return (
    <BranchIOContext.Provider value={value}>
      {children}
    </BranchIOContext.Provider>
  );
};

export const useBranchIO = () => {
  const context = useContext(BranchIOContext);
  if (!context) throw new Error('useBranchIO must be used within a BranchIOProvider');
  return context;
};
